// PDF Report Generator for DCMA Schedule Quality Analysis
// Uses jsPDF to produce a professional multi-page report

import { jsPDF } from 'jspdf';

import type { DCMAScoreResult, CriterionResult, ProjectSummary } from './dcma-scoring-engine';
import type { ExecutiveSummary } from './executive-summary';

interface LeadInfo {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
}

// Color palette (RGB arrays)
const COLORS = {
  primary: [15, 23, 42] as [number, number, number], // slate-900
  secondary: [100, 116, 139] as [number, number, number], // slate-500
  muted: [148, 163, 184] as [number, number, number], // slate-400
  good: [22, 163, 74] as [number, number, number], // green-600
  acceptable: [202, 138, 4] as [number, number, number], // yellow-600
  bad: [220, 38, 38] as [number, number, number], // red-600
  border: [226, 232, 240] as [number, number, number], // slate-200
  bgLight: [248, 250, 252] as [number, number, number], // slate-50
  white: [255, 255, 255] as [number, number, number],
  gradeA: [22, 163, 74] as [number, number, number], // green
  gradeB: [37, 99, 235] as [number, number, number], // blue
  gradeC: [202, 138, 4] as [number, number, number], // amber
  gradeD: [234, 118, 21] as [number, number, number], // orange
  gradeF: [220, 38, 38] as [number, number, number], // red
  // Planera brand colors
  brandBlue: [66, 133, 244] as [number, number, number], // #4285f4
  brandGreen: [208, 248, 53] as [number, number, number], // #d0f835
  brandOrange: [255, 77, 0] as [number, number, number], // #ff4d00
  brandDark: [30, 30, 30] as [number, number, number], // #1e1e1e
};

function getGradeColorRGB(grade: string): [number, number, number] {
  switch (grade) {
    case 'A':
      return COLORS.gradeA;
    case 'B':
      return COLORS.gradeB;
    case 'C':
      return COLORS.gradeC;
    case 'D':
      return COLORS.gradeD;
    case 'F':
      return COLORS.gradeF;
    default:
      return COLORS.muted;
  }
}

function getTierColor(tier: string): [number, number, number] {
  switch (tier) {
    case 'good':
      return COLORS.good;
    case 'acceptable':
      return COLORS.acceptable;
    case 'bad':
    case 'badLow':
    case 'badHigh':
      return COLORS.bad;
    default:
      return COLORS.muted;
  }
}

// ── Drawing helpers ──────────────────────────────────────────────────

function drawScheduleStats(doc: jsPDF, summary: ProjectSummary, x: number, y: number, width: number) {
  const cols = 4;
  const colWidth = width / cols;
  const stats = [
    { label: 'Total Activities', value: summary.totalActivities.toString() },
    { label: 'Relationships', value: summary.totalRelationships.toString() },
    {
      label: 'Rel. Ratio',
      value: (summary.totalRelationships / (summary.nonLOEActivities || 1)).toFixed(2),
    },
    { label: 'Non-LOE Activities', value: summary.nonLOEActivities.toString() },
  ];

  for (let i = 0; i < stats.length; i++) {
    const cx = x + i * colWidth + colWidth / 2;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.primary);
    doc.text(stats[i].value, cx, y, { align: 'center' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.muted);
    doc.text(stats[i].label, cx, y + 6, { align: 'center' });
  }
}

function drawCriterionRow(doc: jsPDF, cr: CriterionResult, x: number, y: number, width: number) {
  // Background
  doc.setFillColor(...COLORS.bgLight);
  doc.roundedRect(x, y - 4, width, 18, 1.5, 1.5, 'F');

  // Tier indicator dot
  const tierColor = getTierColor(cr.tier);
  doc.setFillColor(...tierColor);
  doc.circle(x + 5, y + 2, 2, 'F');

  // Name
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text(cr.criterionName, x + 10, y + 2.5);

  // Value + tier on right
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...tierColor);
  doc.text(`${cr.displayValue}  (${cr.tierLabel})`, x + width - 5, y + 2.5, { align: 'right' });

  // Formula
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.secondary);
  const formulaLines = doc.splitTextToSize(cr.formula, width - 14) as string[];
  doc.text(formulaLines[0], x + 10, y + 8);

  // Score impact
  if (cr.scoreImpact !== 0) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.bad);
    doc.text(`${cr.scoreImpact.toFixed(1)} pts`, x + width - 5, y + 8, { align: 'right' });
  }
}

function groupByCategory(results: CriterionResult[]): [string, CriterionResult[]][] {
  const map = new Map<string, CriterionResult[]>();
  for (const r of results) {
    const label = r.categoryLabel;
    if (!map.has(label)) map.set(label, []);
    map.get(label)!.push(r);
  }
  return Array.from(map.entries());
}

export function generateDCMAReport(
  result: DCMAScoreResult,
  lead: LeadInfo,
  execSummary?: ExecutiveSummary,
): Uint8Array {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let y = 0;

  // ── Helper functions ─────────────────────────────────────────────

  function drawFooter() {
    const pageNum = doc.getNumberOfPages();
    // Thin brand accent line
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.2);
    doc.line(margin, pageHeight - 16, pageWidth - margin, pageHeight - 16);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.muted);
    doc.text(`Page ${pageNum}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    // Planera branding on the left
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.brandDark);
    doc.text('planera', margin, pageHeight - 10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.muted);
    doc.text(' | planera.io', margin + doc.getTextWidth('planera'), pageHeight - 10);
  }

  function checkPageBreak(needed: number) {
    if (y + needed > pageHeight - 25) {
      doc.addPage();
      y = margin;
      drawFooter();
    }
  }

  function drawHorizontalLine(yPos: number) {
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.3);
    doc.line(margin, yPos, pageWidth - margin, yPos);
  }

  function drawSectionTitle(title: string) {
    checkPageBreak(15);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.primary);
    doc.text(title, margin, y);
    y += 3;
    drawHorizontalLine(y);
    y += 6;
  }

  // ── Page 1: Cover / Summary ──────────────────────────────────────

  // Top accent bar with brand colors
  doc.setFillColor(...COLORS.brandDark);
  doc.rect(0, 0, pageWidth, 4, 'F');
  // Colored accent segments on top bar
  const barSegW = 14;
  doc.setFillColor(...COLORS.brandOrange);
  doc.rect(margin, 0, barSegW, 4, 'F');
  doc.setFillColor(...COLORS.brandGreen);
  doc.rect(margin + barSegW, 0, barSegW, 4, 'F');
  doc.setFillColor(...COLORS.brandBlue);
  doc.rect(margin + barSegW * 2, 0, barSegW, 4, 'F');

  // Planera wordmark
  y = 18;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.brandDark);
  doc.text('planera', margin, y);
  // Small dot accent after the logo
  const logoTextWidth = doc.getTextWidth('planera');
  doc.setFillColor(...COLORS.brandBlue);
  doc.circle(margin + logoTextWidth + 2.5, y - 1.5, 1.5, 'F');

  // Title block
  y = 38;
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('DCMA Schedule Quality', margin, y);
  y += 10;
  doc.text('Assessment Report', margin, y);

  y += 14;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.secondary);
  const leadName = `${lead.firstName} ${lead.lastName}`.trim();
  doc.text(`Prepared for ${leadName}${lead.company ? ` at ${lead.company}` : ''}`, margin, y);
  y += 5;
  doc.text(
    `Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
    margin,
    y,
  );

  // Grade circle
  y += 20;
  const gradeX = pageWidth / 2;
  const gradeRadius = 22;
  const gradeColor = getGradeColorRGB(result.grade);
  doc.setFillColor(...gradeColor);
  doc.circle(gradeX, y, gradeRadius, 'F');
  doc.setFontSize(32);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text(result.grade, gradeX, y + 4, { align: 'center' });
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.secondary);
  doc.text(`Score: ${result.totalScore.toFixed(1)} / 100`, gradeX, y + gradeRadius + 8, {
    align: 'center',
  });

  // Project info box
  y += gradeRadius + 22;
  doc.setFillColor(...COLORS.bgLight);
  doc.roundedRect(margin, y, contentWidth, 30, 2, 2, 'F');
  doc.setDrawColor(...COLORS.border);
  doc.roundedRect(margin, y, contentWidth, 30, 2, 2, 'S');

  const boxY = y + 8;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('Project', margin + 6, boxY);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.secondary);
  doc.text(result.projectName, margin + 6, boxY + 5);

  const col2X = margin + contentWidth * 0.35;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('Data Date', col2X, boxY);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.secondary);
  doc.text(result.dataDate ? new Date(result.dataDate).toLocaleDateString() : 'N/A', col2X, boxY + 5);

  const col3X = margin + contentWidth * 0.6;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('Schedule Dates', col3X, boxY);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.secondary);
  const startStr = result.plannedStart ? new Date(result.plannedStart).toLocaleDateString() : 'N/A';
  const finishStr = result.scheduledFinish ? new Date(result.scheduledFinish).toLocaleDateString() : 'N/A';
  doc.text(`${startStr} - ${finishStr}`, col3X, boxY + 5);

  // Schedule statistics
  y += 40;
  drawScheduleStats(doc, result.summary, margin, y, contentWidth);
  y += 35;

  drawFooter();

  // ── Executive Summary Section ────────────────────────────────────

  if (execSummary) {
    doc.addPage();
    y = margin;
    drawFooter();
    drawSectionTitle('Executive Summary');

    // Overall assessment
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.primary);
    const assessLines = doc.splitTextToSize(execSummary.overallAssessment, contentWidth) as string[];
    for (const line of assessLines) {
      checkPageBreak(5);
      doc.text(line, margin, y);
      y += 4.5;
    }
    y += 4;

    // Key findings
    for (const section of execSummary.sections) {
      checkPageBreak(20);

      // Severity indicator
      const sevColor =
        section.severity === 'critical'
          ? COLORS.bad
          : section.severity === 'warning'
            ? COLORS.acceptable
            : COLORS.primary;
      doc.setFillColor(...sevColor);
      doc.rect(margin, y - 3, 1.5, 6, 'F');

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.primary);
      doc.text(section.heading, margin + 5, y);
      y += 6;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.secondary);

      for (const para of section.paragraphs) {
        const paraLines = doc.splitTextToSize(para, contentWidth - 5) as string[];
        for (const line of paraLines) {
          checkPageBreak(5);
          doc.text(line, margin + 5, y);
          y += 4.2;
        }
        y += 2;
      }
      y += 3;
    }

    // Recommendations
    if (execSummary.recommendations.length > 0) {
      checkPageBreak(15);
      drawSectionTitle('Recommendations');

      doc.setFontSize(9);
      for (let i = 0; i < execSummary.recommendations.length; i++) {
        checkPageBreak(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.primary);
        doc.text(`${i + 1}.`, margin + 2, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.secondary);
        const recLines = doc.splitTextToSize(execSummary.recommendations[i], contentWidth - 12) as string[];
        for (const line of recLines) {
          checkPageBreak(5);
          doc.text(line, margin + 10, y);
          y += 4.2;
        }
        y += 2;
      }
      y += 3;
    }

    // Closing statement
    if (execSummary.closingStatement) {
      checkPageBreak(15);
      doc.setFillColor(...COLORS.bgLight);
      const closingLines = doc.splitTextToSize(execSummary.closingStatement, contentWidth - 12) as string[];
      const closingHeight = closingLines.length * 4.5 + 8;
      doc.roundedRect(margin, y, contentWidth, closingHeight, 2, 2, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...COLORS.primary);
      let closingY = y + 6;
      for (const line of closingLines) {
        doc.text(line, margin + 6, closingY);
        closingY += 4.5;
      }
      y += closingHeight + 6;
    }
  }

  // ── Page 2+: Criterion Details ───────────────────────────────────

  doc.addPage();
  y = margin;
  drawFooter();

  drawSectionTitle('Criterion-by-Criterion Analysis');

  // Group by category
  const categories = groupByCategory(result.criterionResults);

  for (const [categoryLabel, criteria] of categories) {
    checkPageBreak(20);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.primary);
    doc.text(categoryLabel, margin, y);
    y += 6;

    for (const cr of criteria) {
      checkPageBreak(30);
      drawCriterionRow(doc, cr, margin, y, contentWidth);
      y += 22;

      // Flagged items (up to 8)
      if (cr.flaggedItems.length > 0) {
        const itemsToShow = cr.flaggedItems.slice(0, 8);
        checkPageBreak(5 + itemsToShow.length * 4.5);

        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(...COLORS.muted);
        doc.text('Flagged items:', margin + 4, y);
        y += 4;

        for (const item of itemsToShow) {
          checkPageBreak(5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...COLORS.secondary);
          const itemText = `${item.activityId} - ${item.activityName}: ${item.reason}`;
          const lines = doc.splitTextToSize(itemText, contentWidth - 8) as string[];
          doc.text(lines[0], margin + 6, y);
          y += 4;
        }

        if (cr.flaggedItems.length > 8) {
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(...COLORS.muted);
          doc.text(`...and ${cr.flaggedItems.length - 8} more`, margin + 6, y);
          y += 4;
        }

        y += 2;
      }
    }

    y += 4;
  }

  // ── Final page: Summary table ────────────────────────────────────

  checkPageBreak(60);
  drawSectionTitle('Score Summary');

  // Header row
  doc.setFillColor(...COLORS.primary);
  doc.rect(margin, y, contentWidth, 7, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text('Criterion', margin + 3, y + 5);
  doc.text('Value', margin + contentWidth * 0.55, y + 5);
  doc.text('Rating', margin + contentWidth * 0.72, y + 5);
  doc.text('Impact', margin + contentWidth * 0.88, y + 5);
  y += 9;

  for (let i = 0; i < result.criterionResults.length; i++) {
    const cr = result.criterionResults[i];
    checkPageBreak(7);

    if (i % 2 === 0) {
      doc.setFillColor(...COLORS.bgLight);
      doc.rect(margin, y - 3.5, contentWidth, 7, 'F');
    }

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.primary);
    doc.text(cr.criterionName, margin + 3, y);

    doc.setTextColor(...COLORS.secondary);
    doc.text(cr.displayValue, margin + contentWidth * 0.55, y);

    const tierColor = getTierColor(cr.tier);
    doc.setTextColor(...tierColor);
    doc.setFont('helvetica', 'bold');
    doc.text(cr.tierLabel, margin + contentWidth * 0.72, y);

    const impactColor = cr.scoreImpact < 0 ? COLORS.bad : COLORS.good;
    doc.setTextColor(...impactColor);
    doc.text(cr.scoreImpact === 0 ? '0' : cr.scoreImpact.toFixed(1), margin + contentWidth * 0.88, y);

    y += 7;
  }

  // Total row
  y += 2;
  drawHorizontalLine(y);
  y += 5;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('Total Score', margin + 3, y);
  doc.text(`${result.totalScore.toFixed(1)} / 100  (${result.grade})`, margin + contentWidth * 0.55, y);

  // Disclaimer
  y += 15;
  checkPageBreak(20);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...COLORS.muted);
  const disclaimer =
    'This report is generated based on the DCMA 14-point assessment methodology. Scores are indicative and should be reviewed alongside professional judgment. Visit planera.io for comprehensive schedule management solutions.';
  const disclaimerLines = doc.splitTextToSize(disclaimer, contentWidth) as string[];
  doc.text(disclaimerLines, margin, y);

  drawFooter();

  return doc.output('arraybuffer') as unknown as Uint8Array;
}
